import * as actions from '../actions/types';
import {
    getCandybarCatalog,
    getCandybarSelectedCategory,
    getCandybarSelectedItem,
    getCheckoutCinema,
    getCheckoutContext,
    getCheckoutStep,
    getCheckoutSteps,
    getCinema,
    getSelectedCinema,
    getUserInfo,
    getUserSignUpPaymentResult
} from '../reducers';
import { getSignUpCinemaId, getSignUpLoyaltyLevel } from '../reducers/auth/signUp';
import {
    AnalyticsCandybar,
    AnalyticsCheckout,
    AnalyticsUsers
} from '../utils/analytics';
import { trackPackagesPurchaseLoad } from '../utils/analytics/packages';
import {
    trackUserSignUpLevelSelect,
    trackUserSignUpBegin,
    trackUserSignUpEmailVerified,
    trackUserSignUpComplete
} from '../utils/analytics/user';
import { trackWalletPurchaseLoad } from '../utils/analytics/wallet';
import {
    trackCheckoutCandybarSelection,
    trackCheckoutConfirmation,
    trackCheckoutPaymentMethodSelection,
    trackCheckoutSessionLoad,
    trackCheckoutTicketsSelection
} from '../utils/analytics/checkout';
import { CHECKOUT_CONTEXT_CANDYBAR } from '../actions/creators/checkout';
import { trackCinemaBillboardLoad } from '../utils/analytics/billboard';

export const analyticsMiddleware = ({ getState, dispatch }) => next => async action => {
    pre(action, getState, dispatch);
    next(action);
    post(action, getState, dispatch);
};

function pre(action, getState, dispatch) {
    switch (action.type) {
        case actions.CHECKOUT_SET_STEP: return onPreCheckoutStepChange(action, dispatch, getState);
        case actions.AUTH_REGISTER_POST_SUCCESS: return onRegisterPostSuccess(action, dispatch, getState);
    }
}

function post(action, getState, dispatch) {
    switch (action.type) {
        case actions.IE_CODE_SUBMIT_SUCCESS: return AnalyticsUsers.trackIeLink('manual');
        case actions.IE_SIGN_UP_SUCCESS: return ieSignUpSuccess(getState, dispatch, action);
        case actions.AUTH_SHOW_REGISTER: return trackUserSignUpBegin();
        case actions.AUTH_SIGN_UP_CHECK_EMAIL_SUCCESS: return trackUserSignUpEmailVerified();
        case actions.AUTH_SIGN_UP_LOYALTY_LEVEL_SELECT: return onUserSignUpLoyaltyLevelSelect(action, getState, dispatch);
        case actions.AUTH_LOGIN_POST_SUCCESS:
        case actions.AUTH_FB_LOGIN_SUCCESS: return (AnalyticsUsers.trackSignIn() & onUserInfoUpdated(action, getState, dispatch));
        case actions.AUTH_LOGOUT_SUCCESS: return AnalyticsUsers.trackSignOut();
        case actions.IE_NIP_UPDATE_SUCCESS: return AnalyticsUsers.trackIeNipChange();
        case actions.CHECKOUT_SESSION_IS_LOADING: return onCheckoutSessionLoad(action, getState, dispatch);
        case actions.CHECKOUT_TICKET_QTY_INCREASE: return AnalyticsCheckout.trackTicketAdd(action.ticket);
        case actions.CHECKOUT_TICKET_QTY_DECREASE: return AnalyticsCheckout.trackTicketRemove(action.ticket);
        case actions.CHECKOUT_PAYMENT_SUCCESS: return trackCheckoutConfirmation(action, getState());
        case actions.APP_LOADED:
        case actions.AUTH_LOGOUT_SUCCESS:
        case actions.AUTH_REGISTER_POST_SUCCESS:
        case actions.CHANGE_PREFERRED_CINEMA: return onUserInfoUpdated(action, getState, dispatch);
        case actions.CHECKOUT_CANDYBAR_IS_LOADING: return onCandybarLoad(action, getState, dispatch);
        case actions.CHECKOUT_CANDYBAR_ITEM_SELECT: return onCandybarItemSelect(action, getState, dispatch);
        case actions.CHECKOUT_CANDYBAR_ITEM_SAVE: return onCandybarItemSave(action, getState, dispatch);
        case actions.WALLET_PURCHASE_LOAD: return trackWalletPurchaseLoad(action);
        case actions.PACKAGES_FETCH_SUCCESS: return onPackagesFetchSuccess(action, getState, dispatch);
        case actions.CINEMA_MOVIES_FETCH_SUCCESS: return trackCinemaBillboardLoad(action, getState());
    }
}

function ieSignUpSuccess(getState, dispatch, {data}) {
    switch (data.action) {
        case 'ie-sign-up': return data.already_registered ? AnalyticsUsers.trackIeLink('sign-up') : AnalyticsUsers.trackIeSignUp(data.level);
        case 'ie-migration': return AnalyticsUsers.trackIeMigration(data.is_current ? 'maintain' : 'upgrade', data.level);
        case 'ie-renewal': return AnalyticsUsers.trackIeRenewal(data.is_current ? 'maintain' : 'upgrade', data.level);
    }
}

function onUserInfoUpdated(action, getState, dispatch) {
    const state = getState();
    const cinema = getSelectedCinema(state);
    const user = getUserInfo(state);

    const data = {
        area: {
            id: null,
            name: null,
        },
        state: {
            id: null,
            name: null,
        },
        cinema: {
            id: null,
            name: null,
            concept: null
        },
        user: {
            type: getUserTypeString(user),
            id: null,
            firstName: null,
            lastName: null,
            birthDate: null,
            phone: null,
            email: null,
            gender: null,
            loyalty: {
                memberId: null,
                level: null,
            },
            preferredCinema: {
                id: null,
                name: null,
                concept: null
            },
        }
    }

    if (cinema) {
        data.cinema = {
            id: cinema.id,
            name: cinema.name,
            concept: getCinemaConceptString(cinema),
        };
        if (cinema.area) {
            data.area = {
                id: cinema.area.id,
                name: cinema.area.name,
            };
        }
        if (cinema.state) {
            data.state = {
                id: cinema.state.id,
                name: cinema.state.name,
            };
        }
    }

    if (user) {
        data.user.id = user.id;
        data.user.firstName = user.first_name;
        data.user.lastName = (user.last_name + ' ' + user.mothers_last_name).replace(/ +/, ' ');
        data.user.birthDate = user.birth_date;
        data.user.phone = user.mobile_number;
        data.user.email = user.email;
        data.user.gender = user.gender;
        data.user.loyalty = {
            memberId: user.iecode,
            level: user.ie_level,
        };

        if (user.cinema_id) {
            const preferredCinema = getCinema(state, user.cinema_id);
            if (preferredCinema) {
                data.user.preferredCinema.id = preferredCinema.id;
                data.user.preferredCinema.name = preferredCinema.name;
                data.user.preferredCinema.concept = getCinemaConceptString(preferredCinema);
            }
        }
    }

    AnalyticsUsers.trackUserInfo(data);

    if ([actions.APP_LOADED, actions.AUTH_LOGIN_POST_SUCCESS, actions.AUTH_FB_LOGIN_SUCCESS].includes(action.type)) {
        AnalyticsUsers.trackUserInfoInitialized(data);
    }
}

function getCinemaConceptString(cinema) {
    if (!cinema) return null;
    if (cinema.platinum === true) return 'platinum';
    if (cinema.market === true) return 'market';
    return 'traditional';
}

function getUserTypeString(user) {
    if (!user || !user.id) return 'guest';
    if (user.iecode) return 'loyalty';
    return 'registered';
}

function onCandybarItemSelect(action, getState, dispatch) {
    const state = getState();
    const item = getCandybarSelectedItem(state);
    const category = getCandybarSelectedCategory(state);
    const cinema = getCheckoutCinema(state);
    
    AnalyticsCandybar.trackProductView(item, category, cinema);
}

function onCandybarItemSave(action, getState, dispatch) {
    const state = getState();
    const category = getCandybarSelectedCategory(state);

    const analyticsTrackMethod = action.unitId
      ? AnalyticsCandybar.trackUpdateCart
      : AnalyticsCandybar.trackAddToCart;
    
    analyticsTrackMethod(action.item, category, action.config);
}

function onPreCheckoutStepChange(action, dispatch, getState) {
    const state = getState();
    const steps = getCheckoutSteps(state);
    const currentStep = getCheckoutStep(state);
    const currentStepIndex = steps.indexOf(currentStep);
    const nextStep = action.step;
    const nextStepIndex = steps.indexOf(nextStep);
    const movingForward = nextStepIndex > currentStepIndex;

    if (!movingForward) {
        return;
    }
    
    onCheckoutStepChange_CurrentStep(currentStep, action, state);
    onCheckoutStepChange_NextStep(nextStep, action, state);
}

function onCheckoutStepChange_CurrentStep(currentStep, action, state) {
    switch (currentStep) {
        case 'tickets': return trackCheckoutTicketsSelection(action, state);
        case 'candybar': return trackCheckoutCandybarSelection(action, state);
    }
}

function onCheckoutStepChange_NextStep(nextStep, action, state) {
    switch (nextStep) {
        case 'payment': return trackCheckoutPaymentMethodSelection(action, state);
    }
}

function onPackagesFetchSuccess(action, getState, dispatch) {
    const state = getState();
    const cinema = getCinema(state, action.cinemaId);
    const packages = action.payload;
    
    trackPackagesPurchaseLoad(cinema, packages);
}

function onUserSignUpLoyaltyLevelSelect(action, getState, dispatch) {
    const { level, cinemaId } = action.payload;
    const cinema = getCinema(getState(), cinemaId);

    trackUserSignUpLevelSelect(cinema, level);
}

function onRegisterPostSuccess(action, dispatch, getState) {
    const state = getState();
    const cinemaId = getSignUpCinemaId(state);
    const cinema = getCinema(state, cinemaId);
    const level = getSignUpLoyaltyLevel(state);
    const result = getUserSignUpPaymentResult(state);

    trackUserSignUpComplete(cinema, level, result);
}

function onCheckoutSessionLoad(action, getState, dispatch) {
    if (action.isLoading) {
        return;
    }
    
    trackCheckoutSessionLoad(action, getState());
}

function onCandybarLoad(action, getState, dispatch) {
    const state = getState();
    if (action.isLoading || CHECKOUT_CONTEXT_CANDYBAR !== getCheckoutContext(state)) {
        return;
    }
    
    const cinema = getCheckoutCinema(state);
    const catalog = getCandybarCatalog(state);
    
    AnalyticsCandybar.trackSectionLoad(cinema, catalog);
}